import { useState, useEffect } from 'react';
import { currencyService, type SupportedCurrency } from '../services/CurrencyService';

interface UseCurrencyConverterProps {
  amount: number;
  fromCurrency: SupportedCurrency;
  toCurrency: SupportedCurrency;
}

interface UseCurrencyConverterResult {
  convertedAmount: number;
  isLoading: boolean;
  error: string | null;
  symbol: string;
}

export const useCurrencyConverter = ({ 
  amount, 
  fromCurrency, 
  toCurrency 
}: UseCurrencyConverterProps): UseCurrencyConverterResult => {
  const [convertedAmount, setConvertedAmount] = useState<number>(amount);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const convertCurrency = async () => {
      if (fromCurrency === toCurrency) {
        setConvertedAmount(amount);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // Convert to USD first if needed, then to target currency
        const usdAmount = fromCurrency === 'USD' 
          ? amount 
          : await currencyService.toUSD(amount, fromCurrency);
        
        const converted = await currencyService.fromUSD(usdAmount, toCurrency);
        setConvertedAmount(converted);
      } catch (err) {
        console.error('Currency conversion error:', err);
        setError(err instanceof Error ? err.message : 'Currency conversion failed');
        // Fall back to original amount on error
        setConvertedAmount(amount);
      } finally {
        setIsLoading(false);
      }
    };

    convertCurrency();
  }, [amount, fromCurrency, toCurrency]);

  return { 
    convertedAmount, 
    isLoading, 
    error,
    symbol: currencyService.getCurrencySymbol(toCurrency)
  };
};